import { GridCellKind, getMiddleCenterBias } from '@glideapps/glide-data-grid';

import type { CustomCell, Rectangle } from '@glideapps/glide-data-grid';

import type { FullTheme } from '@glideapps/glide-data-grid/dist/dts/common/styles';

import type { Kind } from './types';

export const isCustomCellOfKind =
  <T extends CustomCell<{ kind: Kind }>>(kind: T['data']['kind']) =>
  (cell: CustomCell): cell is T => {
    const { data } = cell;
    return 'kind' in data && data.kind === kind;
  };

export const isCustomCellData = (
  data: unknown
): data is CustomCell<{
  kind: Kind;
  value: unknown;
}>['data'] => {
  if (typeof data !== 'object') return false;
  if (data === null) return false;
  return 'value' in data && 'kind' in data;
};

export const isKindOfCustomCell = (cell: unknown): cell is CustomCell => {
  if (typeof cell !== 'object') return false;
  if (cell === null) return false;
  return 'kind' in cell && cell.kind === GridCellKind.Custom;
};

type Args<T> = {
  cellData: T;
  copyData?: string;
  allowOverlay?: boolean;
  readonly?: boolean;
};

/**
 * ## getCustomCellTemplate
 *
 * @param cellData The data to be used in the custom cell
 * @param copyData The data to be copied when the cell is copied by the user
 * @returns A custom cell object of type T
 */
export const getCustomCellTemplate = <T extends CustomCell['data']>({
  cellData,
  copyData = '',
  allowOverlay = true,
  readonly = false,
}: Args<T>): CustomCell<T> => ({
  kind: GridCellKind.Custom,
  allowOverlay,
  readonly,
  copyData,
  data: cellData,
});

type ErrorArgs = {
  ctx: CanvasRenderingContext2D;
  rect: Rectangle;
  theme: FullTheme;
  error: string;
  value?: string;
};

export const drawCellError = ({
  ctx,
  rect,
  theme,
  error,
  value,
}: ErrorArgs) => {
  const valueX = rect.x + theme.cellHorizontalPadding;
  const valueY = rect.y + rect.height / 2 + getMiddleCenterBias(ctx, theme);

  // Draw the value if present
  if (value) {
    ctx.fillStyle = theme.textDark;
    ctx.fillText(value, valueX, valueY - 6);
  }

  // Draw error background highlight
  ctx.beginPath();
  ctx.fillStyle = 'rgba(230, 65, 65, 0.1)';
  ctx.rect(rect.x + 1, rect.y + 1, rect.width, rect.height - 1);
  ctx.fill();

  // Draw the error message
  const errorOffset = 12;
  ctx.fillStyle = 'rgba(230, 65, 65, 1)';
  ctx.fillText(error, valueX, valueY + errorOffset);
};
